import { useState, useRef, useEffect } from 'react';
import { sessions, aiModels } from '../lib/api';
import { useRealtimeAudio } from '../hooks/useRealtimeAudio';

type Phase = 'greeting' | 'conversation' | 'generating' | 'complete';

export default function JarvisChat() {
  const [phase, setPhase] = useState<Phase>('greeting');
  const [isLoading, setIsLoading] = useState(false);
  const [realtimeAvailable, setRealtimeAvailable] = useState(false);
  const [blueprintData, setBlueprintData] = useState<{ title: string; markdown: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Realtime API（sage固定）
  const realtime = useRealtimeAudio();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // 利用可能かチェック
  useEffect(() => {
    aiModels.list().then(res => {
      setRealtimeAvailable(res.realtimeAvailable);
    }).catch(console.error);
  }, []);

  // メッセージが追加されたらスクロール
  useEffect(() => {
    scrollToBottom();
  }, [realtime.messages]);

  // 会話完了時に設計書を自動生成
  useEffect(() => {
    if (realtime.isComplete && phase === 'conversation') {
      handleGenerateBlueprint();
    }
  }, [realtime.isComplete, phase]);

  // セッション開始
  const handleStartSession = async () => {
    setIsLoading(true);
    try {
      await realtime.connect();
      setPhase('conversation');
    } catch (err) {
      console.error('Failed to start session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 設計書生成
  const handleGenerateBlueprint = async () => {
    setPhase('generating');
    setIsLoading(true);
    
    try {
      const conversationText = realtime.messages
        .filter(m => !m.pending)
        .map(m => `${m.role === 'jarvis' ? 'Jarvis' : 'User'}: ${m.content}`)
        .join('\n\n');
      
      const result = await sessions.start({ 
        aiModel: 'openai', 
        aiModelId: 'gpt-4o' 
      });
      
      await sessions.initialIdea(result.sessionId, conversationText);
      const blueprintResult = await sessions.generateBlueprint(result.sessionId);
      
      setBlueprintData({
        title: blueprintResult.title,
        markdown: blueprintResult.markdown,
      });
      
      setPhase('complete');
    } catch (err) {
      console.error('Failed to generate blueprint:', err);
      setPhase('conversation');
    } finally {
      setIsLoading(false);
    }
  };

  // 新しいセッション開始
  const handleNewSession = () => {
    realtime.disconnect();
    realtime.clearMessages();
    setBlueprintData(null);
    setPhase('greeting');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-blue-50/30 flex flex-col">
      {/* ヘッダー */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-100 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl jarvis-avatar flex items-center justify-center">
              <span className="text-white font-bold text-xl">J</span>
            </div>
            <div>
              <h1 className="text-gray-900 font-bold text-lg">Jarvis</h1>
              <p className="text-gray-400 text-xs tracking-wide">System Design Assistant</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* 音声出力ミュート切替 */}
            {phase === 'conversation' && (
              <button
                onClick={() => realtime.setMuted(!realtime.isMuted)}
                className={`p-2.5 rounded-xl transition-all duration-300 ${
                  !realtime.isMuted
                    ? 'bg-blue-500 text-white shadow-blue' 
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
                title={!realtime.isMuted ? '音声出力 ON' : '音声出力 OFF'}
              >
                {!realtime.isMuted ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                )}
              </button>
            )}

            {phase === 'conversation' && (
              <div className="text-right">
                <p className="text-gray-400 text-xs">
                  {realtime.isListening ? '録音中' : realtime.isSpeaking ? '応答中' : '待機中'}
                </p>
                <p className="text-gray-900 text-sm font-medium">
                  {realtime.messages.filter(m => !m.pending).length} メッセージ
                </p>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* メッセージエリア */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* 起動前の画面 */}
          {phase === 'greeting' && (
            <div className="text-center py-16 animate-fade-in">
              {/* Jarvisロゴ */}
              <div className="relative w-32 h-32 mx-auto mb-8">
                <div className="absolute inset-0 rounded-3xl bg-blue-500/20 animate-ping" />
                <div className="absolute inset-2 rounded-3xl bg-blue-500/30 animate-pulse" />
                <div className="relative w-32 h-32 rounded-3xl jarvis-avatar flex items-center justify-center">
                  <span className="text-white font-bold text-5xl">J</span>
                </div>
              </div>
              
              <h2 className="text-gray-900 text-3xl font-bold mb-2">Jarvis</h2>
              <p className="text-gray-500 mb-4">システム設計アシスタント</p>
              <p className="text-gray-400 text-sm mb-10 max-w-md mx-auto">
                音声で会話しながら、あなたのアイデアをシステム設計書に仕上げます
              </p>
              
              {!realtimeAvailable ? (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-4 rounded-2xl max-w-md mx-auto">
                  <p className="font-medium">音声サービスを準備中</p>
                  <p className="text-sm mt-1">APIキーの設定が必要です</p>
                </div>
              ) : (
                <button
                  onClick={handleStartSession}
                  disabled={isLoading}
                  className="btn-primary text-lg px-10 py-4"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-3">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      起動中...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                      </svg>
                      音声セッションを開始
                    </span>
                  )}
                </button>
              )}
            </div>
          )}

          {/* 会話中のメッセージ */}
          {(phase === 'conversation' || phase === 'generating') && (
            <>
              {realtime.messages.map((msg, index) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {msg.role === 'jarvis' && (
                    <div className="w-9 h-9 rounded-xl jarvis-avatar flex items-center justify-center mr-3 flex-shrink-0">
                      <span className="text-white font-bold text-sm">J</span>
                    </div>
                  )}
                  <div className={`${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-bot'} ${msg.pending ? 'opacity-60' : ''}`}>
                    {msg.pending ? (
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span className="text-sm">認識中...</span>
                      </div>
                    ) : (
                      <>
                        <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                        <p className="text-xs opacity-50 mt-2">
                          {msg.timestamp.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              ))}

              {/* Jarvisが話し中 */}
              {realtime.isSpeaking && (
                <div className="flex justify-start animate-fade-in">
                  <div className="w-9 h-9 rounded-xl jarvis-avatar flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold text-sm">J</span>
                  </div>
                  <div className="chat-bubble-bot">
                    <div className="voice-wave">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* 設計書生成中 */}
          {phase === 'generating' && (
            <div className="text-center py-8 animate-fade-in">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-100 flex items-center justify-center">
                <svg className="animate-spin h-8 w-8 text-blue-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">設計書を生成しています...</p>
              <p className="text-gray-400 text-sm mt-1">会話内容を分析中</p>
            </div>
          )}

          {/* 設計書完成 */}
          {phase === 'complete' && blueprintData && (
            <div className="animate-fade-in">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-soft overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                  <h2 className="text-white font-bold text-xl">{blueprintData.title}</h2>
                  <p className="text-blue-100 text-sm mt-1">システム設計書</p>
                </div>
                <div className="p-6">
                  <div className="prose prose-sm max-w-none">
                    <div 
                      className="whitespace-pre-wrap text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: blueprintData.markdown
                          .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-6 mb-3 text-gray-900">$1</h1>')
                          .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold mt-5 mb-2 text-gray-800">$1</h2>')
                          .replace(/^### (.+)$/gm, '<h3 class="text-lg font-medium mt-4 mb-2 text-gray-700">$1</h3>')
                          .replace(/^\- (.+)$/gm, '<li class="ml-4">$1</li>')
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={handleNewSession}
                  className="btn-secondary"
                >
                  新しいセッションを開始
                </button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* 音声コントロール */}
      {phase === 'conversation' && realtime.isConnected && (
        <div className="border-t border-gray-100 bg-white/90 backdrop-blur-lg px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center gap-6">
              {/* 音声可視化リング */}
              <div className="relative flex items-center justify-center">
                {/* 外側のリング */}
                <div
                  className={`absolute rounded-full transition-all duration-300 ${
                    realtime.isListening
                      ? 'w-40 h-40 bg-red-500/10 border-2 border-red-400/30 pulse-ring'
                      : realtime.isSpeaking
                      ? 'w-40 h-40 bg-green-500/10 border-2 border-green-400/30 pulse-ring'
                      : 'w-36 h-36 bg-blue-500/5 border-2 border-blue-200/50'
                  }`}
                />
                
                {/* 中間リング */}
                <div
                  className={`absolute rounded-full transition-all duration-300 ${
                    realtime.isListening
                      ? 'w-32 h-32 bg-red-500/20'
                      : realtime.isSpeaking
                      ? 'w-32 h-32 bg-green-500/20'
                      : 'w-28 h-28 bg-blue-500/10'
                  }`}
                />

                {/* メインのマイクボタン */}
                <button
                  type="button"
                  onClick={realtime.toggleListening}
                  className={`relative z-10 w-24 h-24 mic-button ${
                    realtime.isListening ? 'recording' : 
                    realtime.isSpeaking ? 'speaking' : ''
                  }`}
                >
                  {realtime.isListening ? (
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                    </svg>
                  ) : realtime.isSpeaking ? (
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                  ) : (
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                    </svg>
                  )}
                </button>
              </div>

              {/* ステータステキスト */}
              <div className="text-center">
                <p className={`text-lg font-medium ${
                  realtime.isListening
                    ? 'text-red-500'
                    : realtime.isSpeaking
                    ? 'text-green-500'
                    : 'text-gray-500'
                }`}>
                  {realtime.isListening
                    ? '話しかけてください（自動で認識します）'
                    : realtime.isSpeaking
                    ? 'Jarvisが話しています...'
                    : 'マイクをONにして話しかけてください'}
                </p>
                {realtime.isListening && (
                  <p className="text-sm text-gray-400 mt-1">話し終わると自動で応答します</p>
                )}
              </div>

              {/* 操作ボタン */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleGenerateBlueprint}
                  disabled={realtime.messages.filter(m => !m.pending).length < 2}
                  className="btn-secondary"
                >
                  設計書を生成
                </button>
                <button
                  type="button"
                  onClick={handleNewSession}
                  className="px-5 py-2.5 text-gray-500 hover:text-red-500 text-sm font-medium border border-gray-200 rounded-xl hover:border-red-200 hover:bg-red-50 transition-all"
                >
                  セッションを終了
                </button>
              </div>
              
              {/* エラー表示 */}
              {realtime.error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                  ⚠️ {realtime.error}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
